import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IGroup, Label } from '@fluentui/react';
import { Link } from 'react-router-dom';
import {
  FluentProvider,
  webLightTheme,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { ChevronRightRegular, ChevronDownRegular } from '@fluentui/react-icons';
import { StatUser, GroupIdentifier } from '../../utils/statApi/UsersApi';
import { useCollapsedGroups } from '../roles/useCollapsedGroups';

interface IGraphUserBlockProps {
  statUser: StatUser;
}

const useStyles = makeStyles({
  groupHeader: { display: 'flex', alignItems: 'center' },
});

const SecondaryFormFields = (props: IGraphUserBlockProps): JSX.Element => {
  const { statUser } = props;
  const { collapseGroups, toggleCollapseGroup } = useCollapsedGroups();
  const styles = useStyles();

  const orderedRoles = statUser.userRoles
    ?.slice()
    ?.sort((a, b) => {
      if (a.companyCode) return a.companyCode.localeCompare(b.companyCode);
      if (b.companyCode) return b.companyCode.localeCompare(a.companyCode);
      return 0;
    })
    ?.sort((a, b) => a.groupIdentifier.localeCompare(b.groupIdentifier));

  const getGroups = (): Array<IGroup> => {
    const groups: IGroup[] = [];
    if (orderedRoles) {
      Object.keys(GroupIdentifier).forEach((key) => {
        type groupIdentifierKeys = keyof typeof GroupIdentifier;
        const count = orderedRoles.filter((role) => role.groupIdentifier === key).length;
        const name = GroupIdentifier[key as groupIdentifierKeys];
        if (count > 0) {
          const startIndex = orderedRoles.findIndex((role) => role.groupIdentifier === key);
          groups.push({ key, name, startIndex, count });
        }
      });
    }
    return groups;
  };

  const renderList = (): JSX.Element => {
    if (orderedRoles?.length > 0) {
      return (
        <FluentProvider theme={webLightTheme}>
          <Table aria-label="Current Roles table">
            <TableBody>
              {statUser &&
                (() => {
                  const groups = getGroups();
                  return (
                    groups &&
                    groups.map((group) => {
                      const isCollapsed = !!collapseGroups[group.key] || collapseGroups.All;
                      return (
                        <React.Fragment key={group.key}>
                          <TableRow>
                            <TableCell>
                              <div className={styles.groupHeader}>
                                <Button
                                  appearance="subtle"
                                  size="small"
                                  icon={
                                    isCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />
                                  }
                                  onClick={() => toggleCollapseGroup(group.key)}
                                  aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${
                                    group.name
                                  }`}
                                />
                                <TableCellLayout>
                                  {group.name} ({group.count})
                                </TableCellLayout>
                              </div>
                            </TableCell>
                          </TableRow>
                          {!isCollapsed &&
                            orderedRoles
                              .filter((item) => item.groupIdentifier === group.key)
                              .map((item) => {
                                return (
                                  <TableRow key={item.id}>
                                    <TableCell>
                                      <TableCellLayout style={{ paddingLeft: '32px' }}>
                                        {item.groupIdentifier === 'GLOBAL' ? (
                                          'All Companies'
                                        ) : (
                                          <Link
                                            to={`/companies/${item.companyCode}/service-contacts`}
                                          >
                                            {item.companyCode}
                                          </Link>
                                        )}
                                      </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                      <TableCellLayout>
                                        {item.groupIdentifier === 'COMPANY'
                                          ? item.serviceContactName
                                          : item.roleName}
                                      </TableCellLayout>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                        </React.Fragment>
                      );
                    })
                  );
                })}
            </TableBody>
          </Table>
        </FluentProvider>
      );
    }
    return <span>No roles assigned</span>;
  };

  if (!statUser) {
    return <></>;
  }
  return (
    <Stack>
      <Label>Current Roles</Label>
      {renderList()}
    </Stack>
  );
};

export default SecondaryFormFields;
